import { ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

/**
 * Privacy Policy — verbatim rendering of the operator-approved policy
 * document. Keep the visible text in sync with the operational reality.
 * Bump CURRENT_PRIVACY_VERSION on the backend when this text changes
 * materially so returning users are re-stamped with fresh consent.
 */
export default function Privacy() {
  const today = new Date().toISOString().slice(0, 10);
  const navigate = useNavigate();
  // Back = actual browser back if we have history, else Login (which is
  // the sensible landing spot for an anonymous visitor who reached this
  // page from the Google consent screen or a shared link).
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/login");
  };
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <button
          type="button"
          onClick={goBack}
          data-testid="privacy-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">
          Privacy
        </h1>
        <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
          v2.1 · {today}
        </span>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 space-y-12">
        <section>
          <h2 className="font-prose text-4xl italic leading-tight text-[var(--text)] mb-4">
            The honest version, not the legalese one.
          </h2>
          <p className="font-ui text-base text-[var(--text-muted)] leading-relaxed">
            Humane Behavior AI treats your conversations as yours by
            default. This page describes — in plain English — what we
            store, what we never store, how long we keep it, and who
            else touches it.
          </p>
        </section>

        <Block
          eyebrow="A note on how to read this"
          title="Some of what follows is enforced in code. Some is an operational commitment."
          testid="privacy-note"
        >
          <p>
            Most of what follows is enforced in code: application logic,
            storage rules, access controls, and logging. Some of it is an
            operational commitment by the people running the service
            rather than something a machine prevents. We have marked
            which is which throughout, because a privacy policy that
            blurs the two is worse than one that admits the difference.
            Where a guarantee depends on an external provider, we say so.
          </p>
          <p>
            This service is in beta. Features described here may change;
            when they do, this page changes with them and we tell you.
          </p>
        </Block>

        <Block
          eyebrow="Who we are"
          title="Humane Behavior AI is operated by humanebehavior.com, based in Chennai, India."
          testid="privacy-who-we-are"
        >
          <p>
            For the purposes of India&apos;s Digital Personal Data
            Protection Act, 2023, the operator is the Data Fiduciary.
            Where the EU or UK GDPR applies, the operator is the data
            controller. Contact at{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>
            . If and when the service is transferred to an incorporated
            entity, this section will be updated before the transfer
            takes effect, and you will be notified.
          </p>
        </Block>

        <Block
          eyebrow="Who can use this"
          title="Adults only. 18 and over."
          testid="privacy-who-can-use"
        >
          <p>
            Humane Behavior AI is not intended for, directed to, or
            designed for anyone under 18. Answers are direct and
            unsoftened, and the subject matter is adult. You must confirm
            you are 18 or older to create an account.
          </p>
          <p>
            We do not knowingly collect personal data from anyone
            under 18. If we learn that an account belongs to a minor, we
            delete the account and all associated data — conversations,
            memories, and any training-corpus rows — and we do not
            retain a record beyond what is needed to prevent
            re-registration. If you believe a minor has created an
            account, email{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>{" "}
            and we will act on it immediately.
          </p>
          <p>
            We do not profile users by age, serve advertising, or perform
            behavioural tracking for advertising purposes at all — for
            any user, of any age.
          </p>
        </Block>

        <Block
          eyebrow="Training data"
          title="Your chats are not used to train the model unless you opt in."
          testid="privacy-training"
        >
          <p>
            We will never use a regular user&apos;s conversations to
            train, fine-tune, distill, or evaluate Humane Behavior AI
            without your explicit, revocable permission. The default for
            every new account is <em>off</em>. You turn it on — and off
            — at{" "}
            <Link
              to="/settings/memory"
              className="text-[var(--accent)] hover:underline"
            >
              /settings/memory
            </Link>
            .
          </p>
          <p>
            Consent is collected by a separate, unticked control. Using
            the site does not constitute consent to training-data use,
            and continuing to use the site after we change this policy
            does not either. If we ever want to use your data for a
            materially different purpose, we will ask again.
          </p>
          <p>
            <strong>Anonymous visitors</strong> (no account) are never
            eligible, regardless of any toggle. Anonymous browsing cannot
            produce the informed consent we would need.
          </p>
          <p>
            <strong>One honest limitation.</strong> If you opt in and
            your conversations are used to fine-tune a model, we can
            delete the stored rows on request. We cannot remove the
            influence those rows have already had on model weights that
            were trained before your request — no one currently can.
            Deletion means deletion of your data from our stores, not
            reversal of completed training.
          </p>
        </Block>

        <Block
          eyebrow="Selective memory"
          title="What we remember about you is narrow, durable, and never sensitive."
          testid="privacy-memory"
        >
          <p>
            If you turn on Memory, we extract a short list of durable
            facts from your conversations — your name, the projects you
            work on, how you like to be addressed — and re-use them so
            later answers are less amnesiac.
          </p>
          <p>
            We do not store anything classified as sensitive: health,
            finance, legal, crisis, relationships, sexuality, identity,
            religion. Each conversation is re-scanned before any write to
            enforce this even when our classifier was uncertain at
            retrieval time. <em>(Enforced in code. Classifiers are
            imperfect: if something sensitive is ever stored, delete it
            at{" "}
            <Link
              to="/settings/memory"
              className="text-[var(--accent)] hover:underline"
            >
              /settings/memory
            </Link>{" "}
            or tell us and we will remove it and investigate why it got
            through.)</em>
          </p>
          <p>
            You can view Memory, delete individual entries, or wipe
            everything at{" "}
            <Link
              to="/settings/memory"
              className="text-[var(--accent)] hover:underline"
            >
              /settings/memory
            </Link>
            . Memory is independent of training-data consent — turning
            Memory on does not opt you into training-data use.
          </p>
        </Block>

        <Block
          eyebrow="Conversations about crisis and distress"
          title="Handled differently, and never retained for our benefit."
          testid="privacy-crisis"
        >
          <p>
            If a conversation is classified as involving crisis,
            self-harm, or acute distress:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              It is never written to Memory, regardless of your Memory
              setting.
            </li>
            <li>
              It is never eligible for the training corpus, regardless of
              your training-data consent.
            </li>
            <li>
              No branding, share affordance, or shareable card is
              generated for that exchange.
            </li>
          </ul>
          <p>
            It remains in your own chat history, visible only to you, and
            you can delete it like any other conversation.
          </p>
          <p>
            <strong>What we do not do:</strong> we do not monitor
            conversations in real time, we do not alert anyone on your
            behalf, and we do not contact emergency services for you.
            The model may surface crisis resources within its answer;
            that is generated text, not a human intervention. If you are
            in danger, please contact emergency services or a crisis
            line directly. In India, <strong>Tele-MANAS</strong> is
            available free, 24/7, on <strong>14416</strong>.
          </p>
        </Block>

        <Block
          eyebrow="Chat history"
          title="Your conversation history lives in your account, not in our training corpus."
          testid="privacy-history"
        >
          <p>
            Signed-in users get conversations stored server-side so they
            sync across devices. That storage is for your use —
            re-reading past threads, exporting them, sharing them. Saving
            a conversation to your history does not place it in the
            training corpus; those are two separate stores governed by
            two separate decisions.
          </p>
          <p>
            Anonymous visitors keep conversations only in their
            browser&apos;s local storage. We don&apos;t see them. On
            first sign-in we offer to import them; if you decline, they
            stay browser-local.
          </p>
        </Block>

        <Block
          eyebrow="How long we keep things"
          title="Nothing is kept indefinitely by default."
          testid="privacy-retention"
        >
          <div className="overflow-x-auto -mx-2">
            <table className="w-full border-collapse text-[14px]">
              <thead>
                <tr className="border-b border-[var(--border)] text-left">
                  <th className="py-2 px-2 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] font-normal">
                    What
                  </th>
                  <th className="py-2 px-2 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] font-normal">
                    How long
                  </th>
                </tr>
              </thead>
              <tbody className="text-[var(--text-muted)]">
                <RetentionRow
                  what="Conversations (signed-in)"
                  duration="Until you delete them, or 3 months after your last sign-in"
                />
                <RetentionRow
                  what="Selective memory entries"
                  duration="Until you delete them, or 3 months after your last sign-in"
                />
                <RetentionRow
                  what="Account details"
                  duration="Duration of the account, then 30 days after closure"
                />
                <RetentionRow
                  what="Training-corpus rows (opted-in only)"
                  duration="Until you withdraw and request purge, or 1 month"
                />
                <RetentionRow
                  what="Security and access logs"
                  duration="12 months, then deleted"
                />
                <RetentionRow
                  what="Support correspondence"
                  duration="24 months"
                />
                <RetentionRow
                  what="Anonymous browser-local conversations"
                  duration="We never receive these; they live only on your device"
                />
              </tbody>
            </table>
          </div>
          <p className="mt-5">
            If your account is inactive for 12 months we notify you at
            your registered email before deleting anything. When you
            delete something, it is removed from live systems immediately
            and purged from backups within 30 days.
          </p>
        </Block>

        <Block
          eyebrow="Sharing"
          title="No public sharing without your explicit click and review."
          testid="privacy-share"
        >
          <p>
            We do not produce public links from your conversations
            automatically. Any public-share feature requires you to click
            &ldquo;Create public share link,&rdquo; review the exact text
            that will be published, and confirm. Personal data — email,
            name, attachment filenames, and selective memory context —
            is stripped from the published copy before the link goes
            live.
          </p>
          <p>
            You can revoke a share link at any time; revocation takes
            effect immediately, though we cannot recall copies someone
            already made. Crisis-classified exchanges cannot be shared
            publicly.
          </p>
        </Block>

        <Block
          eyebrow="Third parties, and where your data goes"
          title="We do not sell, rent, or share your data with third parties for their own use."
          testid="privacy-third-parties"
        >
          <p>
            Your conversations, memories, uploaded files, and account
            information are <strong>never</strong> sold, rented, or
            shared with advertisers, data brokers, marketers, or any
            other party for their own purposes. We do not run ads. We do
            not build advertising profiles. We do not license your data
            to model providers for their training.
          </p>
          <p>
            The only third parties involved in operating the site are
            inference-API providers (the LLM APIs that generate answers)
            and infrastructure providers (hosting, database, email
            delivery). Each processes your data only as needed to deliver
            the service, under contractual confidentiality, and is
            contractually prohibited from retaining it for their own
            model training. We do not send them your identity beyond
            what the prompt itself contains.
          </p>
          <p>
            <strong>Cross-border transfer.</strong> Because these
            providers operate outside India, your data is processed
            outside India. We rely on contractual safeguards with each
            provider. Where EU/UK transfers are made, they are made
            under Standard Contractual Clauses or an adequacy decision.
          </p>
          <p>
            <strong>Lawful demands.</strong> The only other exception is
            a lawful, narrowly-scoped request from a competent authority
            that we cannot legally refuse — in which case we respond
            only to the specific data compelled, and we notify you
            unless the request legally prohibits notice.
          </p>
        </Block>

        <Block
          eyebrow="Security and breach notification"
          title="If something goes wrong, you hear it from us."
          testid="privacy-security"
        >
          <p>
            We protect your data with encryption in transit and at rest,
            access controls limiting who can reach production data, and
            audit logging of administrative access.{" "}
            <em>(Enforced in code and infrastructure configuration.)</em>{" "}
            No system is perfectly secure, and we are not going to claim
            otherwise.
          </p>
          <p>If a personal data breach occurs, we will:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              notify affected users without delay, describing what
              happened, what data was involved, and what you should do;
            </li>
            <li>
              notify the Data Protection Board of India within 72 hours,
              and any other regulator where legally required;
            </li>
            <li>publish what we found and what we changed.</li>
          </ul>
          <p>
            Report a suspected breach to{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>
            .
          </p>
        </Block>

        <Block
          eyebrow="Cookies"
          title="Only strictly-necessary cookies."
          testid="privacy-cookies"
        >
          <p>
            Keeping you signed in and protecting the sign-in handshake.
            No advertising, no third-party trackers, no cross-site tracking.
          </p>
        </Block>

        <Block
          eyebrow="First-party analytics"
          title="We log first-party pageviews and clicks so we can improve the product — no third-party trackers, no advertising."
          testid="privacy-analytics"
        >
          <p>
            To understand how the product is used and diagnose problems, we
            record pageviews, click positions on <em>buttons and links only</em>
            (never on inputs or text areas), your device / browser / OS, and
            an approximate geo location derived from your IP. Values you
            type — chats, prompts, form fields — are never captured.
          </p>
          <p className="mt-2">
            <strong>Geo lookup:</strong> we send only your IP address to
            <em> ip-api.com</em> to resolve it to a city and country, then
            cache the result. No other data is shared. If you prefer even
            this lookup not happen, write to
            support@humanebehavior.com and we&apos;ll suppress geo for your
            account.
          </p>
          <p className="mt-2">
            <strong>Retention / product emails:</strong> we may occasionally
            email you from support@humanebehavior.com about your usage
            (e.g., a note if you&apos;re close to your monthly cap) using
            Resend as our email provider. These are transactional, not
            marketing.
          </p>
          <p className="mt-2">
            Analytics data is stored on our own servers and used only by
            our team. You may write to support@humanebehavior.com to
            request deletion of your session history.
          </p>
        </Block>

        <Block
          eyebrow="Automated decisions"
          title="The model generates text. It does not decide anything about you."
          testid="privacy-automated"
        >
          <p>
            It does not make decisions that produce legal or similarly
            significant effects about you — no scoring, ranking,
            eligibility determination, or profiling that feeds a decision
            about you. Everything it produces is output for you to read
            and judge.
          </p>
        </Block>

        <Block
          eyebrow="AI can be wrong"
          title="Every answer is a probabilistic guess. Verify before you rely on it."
          testid="privacy-ai-wrong"
        >
          <p>
            The AI can produce answers that are factually wrong,
            ethically questionable, or entirely invented — sometimes in
            a very confident tone. Names, citations, statistics, and
            quotes may not correspond to anything real. Do not use the
            output as medical, psychological, legal, financial, or
            safety advice. Always verify before you act. See the{" "}
            <Link
              to="/terms"
              className="text-[var(--accent)] hover:underline"
            >
              Terms of Use
            </Link>{" "}
            for the full disclaimer and liability terms.
          </p>
        </Block>

        <Block
          eyebrow="Your rights"
          title="Access, correction, erasure, withdrawal, nomination, and complaint."
          testid="privacy-rights"
        >
          <p>
            <strong>Right to access.</strong> You can see every
            conversation, every stored memory, and every account detail
            we hold, from inside the app.
          </p>
          <p>
            <strong>Right to correction.</strong> If anything we hold
            about you is inaccurate, incomplete, or out of date —
            including a Memory entry — you can correct it in the app or
            ask us to.
          </p>
          <p>
            <strong>Right to erasure.</strong> You can delete any
            conversation, any memory, or your entire account. Full
            account erasure includes historical training-corpus rows on
            request. Subject to the limitation noted under Training data
            regarding model weights.
          </p>
          <p>
            <strong>Right to withdraw consent.</strong> Turn the
            training-data toggle off at{" "}
            <Link
              to="/settings/memory"
              className="text-[var(--accent)] hover:underline"
            >
              /settings/memory
            </Link>{" "}
            at any time. Withdrawal is as easy as giving consent. From
            that moment no new rows enter the training corpus; email us
            to also purge rows saved while consent was on.
          </p>
          <p>
            <strong>Right to nominate.</strong> Under the DPDP Act you
            may nominate someone to exercise these rights on your behalf
            in the event of your death or incapacity. Email{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>{" "}
            to register a nominee.
          </p>
          <p>
            <strong>Right to complain.</strong> See below.
          </p>
        </Block>

        <Block
          eyebrow="Grievance redressal"
          title="Every privacy concern is reviewed by a named human, the same business day."
          testid="privacy-grievance"
        >
          <p>
            If something looks wrong — data appearing where it
            shouldn&apos;t, a consent setting not being respected, an
            unexpected use of your conversations — email{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>
            . A human reviews it the same business day and responds with
            what happened and what we are doing about it. We aim to
            resolve within 30 days.
          </p>
          <p>
            Internally, the service is designed so violations of the
            rules above are hard to introduce accidentally and detectable
            when they occur. We keep an audit trail so any concern can
            be traced, explained, and corrected.
          </p>
        </Block>

        <Block
          eyebrow="Language"
          title="This policy is published in English."
          testid="privacy-language"
        >
          <p>
            If you would prefer it in Tamil, Hindi, or another language
            in the Eighth Schedule to the Constitution of India, email{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>{" "}
            and we will provide it.
          </p>
        </Block>

        <Block
          eyebrow="Changes to this policy"
          title="We update this page as the product evolves and as the law changes."
          testid="privacy-changes"
        >
          <p>
            Material changes are flagged at the top and notified by
            email to registered users. Continued use is not consent.
            Where a change materially alters what we do with your
            personal data, we ask for fresh consent rather than assuming
            it from your silence. If you do not agree, you can close
            your account and take your data with you.
          </p>
        </Block>

        <section className="border-t border-[var(--border)] pt-8">
          <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            Questions about this policy?{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="hover:text-[var(--accent)]"
            >
              support@humanebehavior.com
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}

function Block({ eyebrow, title, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--accent)] mb-3">
        {eyebrow}
      </div>
      <h2 className="font-prose text-2xl md:text-3xl text-[var(--text)] leading-tight mb-5">
        {title}
      </h2>
      <div className="space-y-4 font-ui text-[15px] text-[var(--text-muted)] leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function RetentionRow({ what, duration }) {
  return (
    <tr className="border-b border-[var(--border-soft)] last:border-b-0">
      <td className="py-3 px-2 align-top w-1/2">{what}</td>
      <td className="py-3 px-2 align-top">{duration}</td>
    </tr>
  );
}
